import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Checkbox,
  Modal,
  Spin,
  message,
} from 'antd';

import { captureAnalytics } from '../../actions/preload';
import ModalWrapper from './ModalWrapper';
import AssignRecordFields from './AssignRecordFields';
import { fetchAssigneeOptions } from './assigneeSearch';
import {
  ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE,
  hasFeatureInLocalStorage,
  assignModalTitle,
  assignmentStaysOpenNote,
  fetchArchiveClosesAssignments,
} from '../../global/featureFlags';
import { archiveFailed } from '../../util/archiveOutcome';
import './assignRecord.scss';

// Same guarded shape as useSetupAssistantFlow's and DynamicEhrDrawer's: crypto
// .randomUUID is absent on older browsers and outside a secure context, and an
// unguarded call would throw and cost the user the assignment entirely. The
// fallback only has to be unique per caller per action -- the server treats the
// value as opaque.
const mintActionId = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const { confirm } = Modal;

// ENG-3409. Assigns every selected record to one user in a single action.
//
// Separate from AssignRecord rather than an array mode on it: that component also
// serves Mark Complete and Reassign from three other surfaces and is written
// throughout around a single send token.
export const AssignMultipleRecords = (props) => {
  const {
    allDocumentStatuses,
    assignDocumentsBulk,
    archiveReceivedRecords,
    getDocumentStatuses,
    getUserDepartmentsByQuery,
    hideModal,
    isLoading,
    modalType,
    refreshInboxAfterAssign,
    rowSelected,
  } = props;

  const records = modalType.data.records || [];
  const rows = modalType.data.rows || [];

  const [selectedDocumentStatus, setSelectedDocumentStatus] = useState(null);
  const [assignee, setAssignee] = useState(undefined);
  const [note, setNote] = useState('');
  // Tri-state like AssignRecord's: null until the setting answers, because the
  // note makes a categorical claim about archive behavior.
  const [archiveClosesAssignments, setArchiveClosesAssignments] = useState(null);
  const [archiveAfterAssign, setArchiveAfterAssign] = useState(false);

  // One action id for this modal's whole assignment, minted on the first attempt
  // and REUSED by every retry of it. Bulk assignment is additive, so a request that
  // commits and loses its response would otherwise be assigned a second time when
  // the user tries again; the server reconciles the repeat on this id instead.
  // A ref, not state: it must not re-render, and it must survive the re-entrant
  // submit(true) the access confirmation makes.
  const actionId = useRef(null);

  // An action id identifies ONE assignment, not one modal. The ambiguous-failure
  // branch below deliberately leaves the modal open with the assignee, status and
  // note still editable -- and editing any of them makes the next Submit a
  // DIFFERENT action, which must not carry the previous one's id. Reusing it there
  // collides with whatever the first attempt committed, and the server can only
  // answer that the records already belong to the ORIGINAL assignee.
  //
  // Clearing the ref rather than minting now: the id is minted on submit, so an
  // edit that is never submitted costs nothing.
  useEffect(() => {
    actionId.current = null;
  }, [assignee, selectedDocumentStatus, note]);

  // Read from the store on every render: copying into state in the fetch callback
  // closes over the initial {} from a fresh session, so statuses never appear.
  const documentStatuses = allDocumentStatuses.document_statuses || [];

  useEffect(() => {
    let mounted = true;

    fetchArchiveClosesAssignments().then((closes) => {
      if (mounted) setArchiveClosesAssignments(closes);
    });
    getDocumentStatuses();

    return () => { mounted = false; };
    // Mount only: the result arrives through the store, and the archive setting
    // does not change while the modal is open.
  }, []);

  const fetchUserList = useCallback(
    (value) => fetchAssigneeOptions({ value, getUserDepartmentsByQuery }),
    [getUserDepartmentsByQuery],
  );

  const assigneeName = assignee ? (assignee.label || assignee.value) : '';

  const reportOutcome = (created, skipped) => {
    if (!created) {
      message.error({ content: 'None of the selected records could be assigned. Please try again.' });
      return;
    }

    if (skipped) {
      // A warning, not an error: what was assigned is committed, and the copy must
      // not suggest otherwise.
      message.warning({
        content: `Assigned ${created} of ${created + skipped} records to ${assigneeName}. `
          + `${skipped} could not be assigned and ${skipped === 1 ? 'remains' : 'remain'} selected.`,
      });
    } else {
      message.success({ content: `Assigned ${created} records to ${assigneeName}` });
    }
  };

  const archiveAssignedRecords = async (skipped) => {
    // Archive accepts tokens, so a token shared with a skipped record must stay
    // untouched even if another selected record with that token was assigned.
    const skippedTokens = new Set(skipped.map((entry) => entry.send_token));
    const assignedTokens = new Set();
    records.forEach(({ send_token: token }) => {
      if (!skippedTokens.has(token)) assignedTokens.add(token);
    });
    const tokens = [...assignedTokens];
    if (!tokens.length) return;

    // Recheck immediately before archiving, as in the single-record flow.
    const closesAssignments = await fetchArchiveClosesAssignments();
    if (closesAssignments !== false) {
      message.warning({ content: 'Records assigned, but not archived because archiving may close assignments.' });
      return;
    }
    const result = await archiveReceivedRecords(tokens, true);
    if (archiveFailed(result)) {
      message.error({ content: `Records assigned, but not archived. ${result?.response?.data?.error || 'Please try again.'}` });
      return;
    }
    message.success({ content: 'Assigned records archived successfully' });
  };

  // Decisions come off the response, not redux: `props` is the render-time
  // snapshot, so a store value read after the await is the pre-request one.
  const submit = async (forceAssign) => {
    if (!actionId.current) actionId.current = mintActionId();

    const payload = {
      assignee_id: assignee.value,
      document_status_id: selectedDocumentStatus,
      note,
      force_assign: forceAssign,
      idempotency_key: actionId.current,
      records,
    };

    const response = await assignDocumentsBulk(payload);

    // The modal stays OPEN here, deliberately. This branch covers the ambiguous
    // failure -- the request may have committed and lost its response -- and the
    // retry is only safe while `actionId` is still in hand. Closing would discard
    // it, so the next attempt would be a fresh action the server cannot reconcile.
    if (!response || response.error) {
      message.error({ content: 'Error assigning records. Press Submit to try again.' });
      return;
    }

    // Asked once for the whole batch: access turns only on whether the assignee
    // shares a department with this user. Nothing has been assigned yet.
    if (response.data?.is_department_user === false) {
      confirm({
        title: 'This person doesn\'t have access!',
        okText: 'Yes',
        cancelText: 'No',
        content: 'This user doesn\'t currently have access to the document. Would you like to grant them access to complete this assignment?',
        onOk: () => submit(true),
      });
      return;
    }

    // The endpoint names the items it did not assign, rather than counting them.
    const { created, skipped = [] } = response.data || {};

    // Counts only: the note is free text typed over a patient's record.
    captureAnalytics('record_assign_bulk', {
      count: records.length,
      created,
      skipped: skipped.length,
      forceAssign: forceAssign === true,
    });

    hideModal();
    reportOutcome(created, skipped.length);
    // Keep exactly the rows that were not assigned selected, so a partial batch is
    // retried by repeating the action; 'none' clears the selection outright when
    // everything landed. Then refetch so the assignee column reflects what actually
    // happened.
    // Matched on the pair, not the token alone: a send_token can be shared with an
    // archived twin, so the token by itself would re-select the wrong row.
    const unassignedRows = rows.filter((row) => skipped.some((entry) => (
      entry.send_token === row.send_token && entry.record_id === row.file_token
    )));
    if (archiveAfterAssign && created > 0) await archiveAssignedRecords(skipped);
    // The archive success callback clears selection when background refresh is
    // enabled. Restore skipped rows only after that callback has finished.
    rowSelected(unassignedRows.length > 0 ? unassignedRows : ['none']);
    refreshInboxAfterAssign();
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="assign-record-modal__loading">
          <Spin size="small" />
          <span>Assigning records…</span>
        </div>
      );
    }

    const archivingLeavesAssignmentsOpen = archiveClosesAssignments === false;
    const staysOpenNote = archivingLeavesAssignmentsOpen && !archiveAfterAssign ? assignmentStaysOpenNote() : null;

    return (
      <AssignRecordFields
        formId="assignMultipleRecords"
        summary={`Assigning ${records.length} ${records.length === 1 ? 'record' : 'records'}.`}
        staysOpenNote={staysOpenNote}
        documentStatuses={documentStatuses}
        selectedDocumentStatus={selectedDocumentStatus}
        onDocumentStatusChange={(value) => setSelectedDocumentStatus(value)}
        assignee={assignee}
        fetchUserList={fetchUserList}
        onAssigneeChange={(value) => setAssignee(value)}
        note={note}
        onNoteChange={setNote}
      />
    );
  };

  return (
    <ModalWrapper
      hideModal={hideModal}
      customContentStyle={{}}
      width="560px"
      action={() => submit(false)}
      actionName="Submit"
      dismiss={isLoading ? '' : 'Cancel'}
      disabled={isLoading || !selectedDocumentStatus || !assignee}
      modalTitle={assignModalTitle({ markComplete: false })}
      form="assignMultipleRecords"
      wrapClassName="assign-record-modal"
      footerLeading={hasFeatureInLocalStorage(ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE) && archiveClosesAssignments === false && records.every((record) => !['reply', 'Reply'].includes(record.parent_type)) && (
        <Checkbox
          checked={archiveAfterAssign}
          disabled={isLoading}
          onChange={(event) => {
            setArchiveAfterAssign(event.target.checked);
            captureAnalytics('assign_archive_after_toggled', { enabled: event.target.checked, bulk: true });
          }}
        >
          Archive after assigning
        </Checkbox>
      )}
    >
      {renderBody()}
    </ModalWrapper>
  );
};

AssignMultipleRecords.propTypes = {
  allDocumentStatuses: PropTypes.object.isRequired,
  assignDocumentsBulk: PropTypes.func.isRequired,
  archiveReceivedRecords: PropTypes.func.isRequired,
  getDocumentStatuses: PropTypes.func.isRequired,
  getUserDepartmentsByQuery: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  modalType: PropTypes.object.isRequired,
  refreshInboxAfterAssign: PropTypes.func.isRequired,
  rowSelected: PropTypes.func.isRequired,
};

export default connect((state) => ({
  isLoading: state.records.isLoading,
  allDocumentStatuses: state.userProfile.allDocumentStatuses,
}))(AssignMultipleRecords);
